import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import SearchableDropdown from './SearchableDropdown';
import QuickAddModal from './QuickAddModal_Dropdown';
import SeaCostModal from './SeaCostModal';
import LandCostModal from './LandCostModal';
import NotificationChoiceModal from './NotificationChoiceModal';
import '../styles/nassar-theme.css';
import './AddClearanceFileModal.css';

const AddClearanceFileModal = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        customer: '',
        file_num: '',
        goods: '',
        destination_port: '',
        shipment_type: '',
        shipment_company: '',
        expected_arrival_date: '',
        inspection_date: '',
        inspection_time: '',
        file_status: '',
        driver: '',
        origin_country: '',
        custom_dec_num: '',
        authority: '',
        file_type: '',
        cost: '0'
    });

    // Cost calculation modal state
    const [costModal, setCostModal] = useState({
        isOpen: false,
        type: '' // 'SEA' or 'LAND'
    });
    
    // Cost calculation data to save after file is created
    const [costData, setCostData] = useState(null);

    // Custom date inputs state
    const [arrivalDate, setArrivalDate] = useState({ day: '', month: '', year: '' });
    const [inspectionDate, setInspectionDate] = useState({ day: '', month: '', year: '' });
    const [inspectionTime, setInspectionTime] = useState({ hour: '', minute: '' });

    const [lookupData, setLookupData] = useState({
        customers: [],
        goods: [],
        destination_ports: [],
        shipment_types: [],
        file_statuses: [],
        file_types: [],
        container_types: [],
        shipment_companies: [],
        drivers: [],
        origin_countries: [],
        authorities: []
    });

    const [containers, setContainers] = useState([]);

    const [quickAddModal, setQuickAddModal] = useState({
        isOpen: false,
        type: '',
        title: '',
        fieldLabel: '',
        endpoint: '',
        fields: null,
        editMode: false,
        editId: null
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        item: null,
        endpoint: '',
        hasError: false
    });
    
    // WhatsApp notification choice modal state
    const [notificationChoiceModal, setNotificationChoiceModal] = useState({
        isOpen: false,
        notificationType: '', // 'new-file' | 'inspection' | 'gate-pass'
        notificationData: null
    });

    useEffect(() => {
        if (isOpen) {
            fetchLookupData();
        }
    }, [isOpen]);

    const fetchLookupData = async () => {
        try {
            const [customers, goods, ports, shipmentTypes, statuses, fileTypes, containerTypes, shipmentCompanies, drivers, originCountries, authorities] = await Promise.all([
                axiosInstance.get('/customers/'),
                axiosInstance.get('/goods/'),
                axiosInstance.get('/destination-ports/'),
                axiosInstance.get('/shipment-types/'),
                axiosInstance.get('/file-statuses/'),
                axiosInstance.get('/file-types/'),
                axiosInstance.get('/container-types/'),
                axiosInstance.get('/shipment-companies/'),
                axiosInstance.get('/drivers/'),
                axiosInstance.get('/origin-countries/'),
                axiosInstance.get('/authorities/')
            ]);

            // Sort all data alphabetically
            const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '', 'ar');
            const sortByDescription = (a, b) => (a.description || '').localeCompare(b.description || '', 'ar');

            setLookupData({
                customers: customers.data.sort(sortByName),
                goods: goods.data.sort(sortByDescription),
                destination_ports: ports.data.sort(sortByName),
                shipment_types: shipmentTypes.data.sort(sortByName),
                file_statuses: statuses.data.sort(sortByName),
                file_types: fileTypes.data.sort(sortByName),
                container_types: containerTypes.data.sort(sortByName),
                shipment_companies: shipmentCompanies.data.sort(sortByName),
                drivers: drivers.data.sort(sortByName),
                origin_countries: originCountries.data.sort(sortByName),
                authorities: authorities.data.sort(sortByName)
            });
        } catch (err) {
            console.error('Error fetching lookup data:', err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (error) setError('');
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const handleDropdownChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const handleContainerChange = (index, field, value) => {
        const updatedContainers = [...containers];
        updatedContainers[index][field] = value;
        setContainers(updatedContainers);
        if (error) setError('');
        // Clear validation error for this container field
        const errorKey = `container_${index}_${field}`;
        if (validationErrors[errorKey]) {
            setValidationErrors(prev => ({ ...prev, [errorKey]: false }));
        }
    };

    // Custom date handling functions
    const handleDateChange = (dateType, field, value) => {
        // Allow only numbers
        const numericValue = value.replace(/\D/g, '');
        
        let maxLength = field === 'year' ? 4 : 2;
        let maxValue = field === 'day' ? 31 : field === 'month' ? 12 : 9999;
        let truncatedValue = numericValue.slice(0, maxLength);
        
        // Validate max values
        if (truncatedValue && parseInt(truncatedValue) > maxValue) {
            truncatedValue = String(maxValue);
        }
        
        if (dateType === 'arrival') {
            const newDate = { ...arrivalDate, [field]: truncatedValue };
            setArrivalDate(newDate);
            updateFormDataDate('expected_arrival_date', newDate);
            // Clear validation error
            if (validationErrors.expected_arrival_date) {
                setValidationErrors(prev => ({ ...prev, expected_arrival_date: false }));
            }
        } else {
            const newDate = { ...inspectionDate, [field]: truncatedValue };
            setInspectionDate(newDate);
            updateFormDataDate('inspection_date', newDate);
            // Clear validation error
            if (validationErrors.inspection_date) {
                setValidationErrors(prev => ({ ...prev, inspection_date: false }));
            }
        }
        if (error) setError('');
    };

    const updateFormDataDate = (fieldName, dateObj) => {
        if (dateObj.day && dateObj.month && dateObj.year && dateObj.year.length === 4) {
            const formattedDate = `${dateObj.year}-${dateObj.month.padStart(2, '0')}-${dateObj.day.padStart(2, '0')}`;
            setFormData(prev => ({ ...prev, [fieldName]: formattedDate }));
        } else {
            setFormData(prev => ({ ...prev, [fieldName]: '' }));
        }
    };

    const handleTimeChange = (field, value) => {
        const numericValue = value.replace(/\D/g, '');
        let maxValue = field === 'hour' ? 23 : 59;
        let truncatedValue = numericValue.slice(0, 2);
        
        if (truncatedValue && parseInt(truncatedValue) > maxValue) {
            truncatedValue = String(maxValue);
        }
        
        const newTime = { ...inspectionTime, [field]: truncatedValue };
        setInspectionTime(newTime);
        
        if (newTime.hour && newTime.minute) {
            const formattedTime = `${newTime.hour.padStart(2, '0')}:${newTime.minute.padStart(2, '0')}`;
            setFormData(prev => ({ ...prev, inspection_time: formattedTime }));
            // Clear validation error
            if (validationErrors.inspection_time) {
                setValidationErrors(prev => ({ ...prev, inspection_time: false }));
            }
        } else {
            setFormData(prev => ({ ...prev, inspection_time: '' }));
        }
        if (error) setError('');
    };

    const handleDatePickerChange = (dateType, e) => {
        const value = e.target.value;
        if (value) {
            const [year, month, day] = value.split('-');
            if (dateType === 'arrival') {
                setArrivalDate({ day, month, year });
                setFormData(prev => ({ ...prev, expected_arrival_date: value }));
            } else {
                setInspectionDate({ day, month, year });
                setFormData(prev => ({ ...prev, inspection_date: value }));
            }
        }
        if (error) setError('');
    };

    const addContainer = () => {
        setContainers([...containers, { number: '', container_type: '', cargo_weight: '', package_count: '' }]);
    };

    const removeContainer = (index) => {
        const updatedContainers = containers.filter((_, i) => i !== index);
        setContainers(updatedContainers);
    };

    const openQuickAdd = (type, title, fieldLabel, endpoint, fields = null) => {
        setQuickAddModal({
            isOpen: true,
            type,
            title,
            fieldLabel,
            endpoint,
            fields,
            editMode: false,
            editId: null
        });
    };

    const handleEdit = (type, title, fieldLabel, endpoint, fields, item) => {
        setQuickAddModal({
            isOpen: true,
            type,
            title: `تعديل ${title.replace('إضافة ', '').replace(' جديدة', '').replace(' جديد', '').replace('جديدة', '').replace('جديد', '').trim()}`,
            fieldLabel,
            endpoint,
            fields,
            editMode: true,
            editId: item.id,
            editData: item
        });
    };

    const handleDelete = (endpoint, item) => {
        // Check if the item is currently selected in the form
        let isInUse = false;
        let usageMessage = '';
        
        // Map endpoints to their form fields
        const endpointFieldMap = {
            '/customers/': { field: 'customer', label: 'الزبون' },
            '/goods/': { field: 'goods', label: 'وصف البضاعة' },
            '/destination-ports/': { field: 'destination_port', label: 'ميناء الوجهة' },
            '/shipment-types/': { field: 'shipment_type', label: 'نوع الشحنة' },
            '/file-statuses/': { field: 'file_status', label: 'حالة الملف' },
            '/file-types/': { field: 'file_type', label: 'نوع الملف' },
            '/container-types/': { field: 'container_type', label: 'نوع الحاوية' },
            '/shipment-companies/': { field: 'shipment_company', label: 'شركة الشحن' },
            '/drivers/': { field: 'driver', label: 'الشفير' },
            '/origin-countries/': { field: 'origin_country', label: 'بلد المنشأ' },
            '/authorities/': { field: 'authority', label: 'المخلص' }
        };
        
        const fieldInfo = endpointFieldMap[endpoint];
        
        if (fieldInfo) {
            // Check if selected in main form
            if (fieldInfo.field !== 'container_type' && formData[fieldInfo.field] === item.id) {
                isInUse = true;
                usageMessage = `لا يمكن حذف "${item.name || item.description}" لأنه محدد حالياً في حقل "${fieldInfo.label}"`;
            }
            
            // Check container_type usage in containers array
            if (fieldInfo.field === 'container_type' && containers.length > 0) {
                const containerUsingThisType = containers.findIndex(c => c.container_type === item.id);
                if (containerUsingThisType !== -1) {
                    isInUse = true;
                    usageMessage = `لا يمكن حذف "${item.name}" لأنه مستخدم في الحاوية رقم ${containerUsingThisType + 1}`;
                }
            }
        }
        
        if (isInUse) {
            // Show warning message without proceeding to delete
            setError(usageMessage);
            setDeleteModal({
                isOpen: true,
                item,
                endpoint,
                hasError: true
            });
        } else {
            setDeleteModal({
                isOpen: true,
                item,
                endpoint,
                hasError: false
            });
        }
    };

    const confirmDelete = async () => {
        const { endpoint, item } = deleteModal;
        try {
            // Try to delete the item
            await axiosInstance.delete(`${endpoint}${item.id}/`);
            // If successful, refresh the data and close modal
            await fetchLookupData();
            closeDeleteModal();
        } catch (err) {
            // If error (item is used), show error in the same modal
            const errorData = err.response?.data;
            if (errorData?.error) {
                setError(errorData.error);
                setDeleteModal(prev => ({ ...prev, hasError: true }));
            } else {
                setError('حدث خطأ أثناء الحذف');
                setDeleteModal(prev => ({ ...prev, hasError: true }));
            }
        }
    };

    const closeDeleteModal = () => {
        setDeleteModal({ isOpen: false, item: null, endpoint: '', hasError: false });
        setError('');
    };


    const handleQuickAddSave = async (data) => {
        const { endpoint, editMode, editId } = quickAddModal;
        
        let response;
        if (editMode && editId) {
            // Update existing item
            response = await axiosInstance.put(`${endpoint}${editId}/`, data);
        } else {
            // Create new item
            response = await axiosInstance.post(endpoint, data);
        }
        
        // Refresh the specific lookup data
        await fetchLookupData();
        
        // Auto-select the newly added/edited item
        const fieldMap = {
            '/customers/': 'customer',
            '/goods/': 'goods',
            '/destination-ports/': 'destination_port',
            '/shipment-types/': 'shipment_type',
            '/file-statuses/': 'file_status',
            '/file-types/': 'file_type',
            '/container-types/': 'container_type',
            '/shipment-companies/': 'shipment_company',
            '/drivers/': 'driver',
            '/origin-countries/': 'origin_country',
            '/authorities/': 'authority'
        };
        
        const fieldName = fieldMap[endpoint];
        if (fieldName && response.data.id) {
            // For container_type, we don't auto-select in formData
            if (fieldName !== 'container_type') {
                setFormData(prev => ({ ...prev, [fieldName]: response.data.id }));
            }
        }
        
        setQuickAddModal({ ...quickAddModal, isOpen: false });
    };

    // Handle cost calculation button click
    const handleCalculateCost = () => {
        // Check file type
        const missingFileType = !formData.file_type;

        // Only set validation errors for empty fields
        if (missingFileType) {
            const newErrors = {};
            if (missingFileType) newErrors.file_type = true;
            setValidationErrors(prev => ({ ...prev, ...newErrors }));
            setError('الرجاء اختيار نوع الملف أولاً');
            return;
        }

        // Get file type name
        const selectedFileType = lookupData.file_types.find(ft => ft.id === formData.file_type);
        const fileTypeName = (selectedFileType?.name || '').toUpperCase();
        
        // Debug: log the file type to console
        console.log('Selected file type:', selectedFileType, 'Name:', fileTypeName);

        // Check for SEA/Maritime type
        if (fileTypeName.includes('SEA') || fileTypeName.includes('بحري') || fileTypeName.includes('MARITIME') || fileTypeName.includes('بحر')) {
            setError('');
            setCostModal({ isOpen: true, type: 'SEA' });
        } 
        // Check for AIR type (uses same modal as SEA)
        else if (fileTypeName.includes('AIR') || fileTypeName.includes('جوي') || fileTypeName.includes('جو')) {
            setError('');
            setCostModal({ isOpen: true, type: 'AIR' });
        }
        // Check for LAND type
        else if (fileTypeName.includes('LAND') || fileTypeName.includes('بري') || fileTypeName.includes('أرضي') || fileTypeName.includes('بر')) {
            setError('');
            setCostModal({ isOpen: true, type: 'LAND' });
        } 
        else {
            // Show the current file type name in the error for debugging
            setError(`نوع الملف "${selectedFileType?.name || 'غير معروف'}" غير مدعوم. يجب أن يكون بحري (SEA) أو جوي (AIR) أو بري (LAND)`);
            return;
        }
    };

    // Apply calculated cost from modal
    const handleApplyCost = (totalCost, costCalculationData) => {
        console.log('handleApplyCost called with:', { totalCost, costCalculationData, modalType: costModal.type });
        setFormData(prev => ({ ...prev, cost: String(Math.floor(totalCost || 0)) }));
        // Store the cost data with the type for saving later
        setCostData({ type: costModal.type, data: costCalculationData });
        setCostModal({ isOpen: false, type: '' });
    };

    const validateForm = () => {
        // Only these fields are required in the backend model
        const requiredFields = [
            'customer', 'goods', 'file_type'
        ];

        const errors = {};
        let hasErrors = false;

        for (const field of requiredFields) {
            if (!formData[field] || formData[field] === '') {
                errors[field] = true;
                hasErrors = true;
            }
        }

        // Validate containers only if there are any
        if (containers.length > 0) {
            containers.forEach((container, index) => {
                if (!container.number) {
                    errors[`container_${index}_number`] = true;
                    hasErrors = true;
                }
                if (!container.container_type) {
                    errors[`container_${index}_container_type`] = true;
                    hasErrors = true;
                }
                if (!container.cargo_weight) {
                    errors[`container_${index}_cargo_weight`] = true;
                    hasErrors = true;
                }
                if (!container.package_count) {
                    errors[`container_${index}_package_count`] = true;
                    hasErrors = true;
                }
            });
        }

        // Validate inspection date and time - both must be provided together
        const hasInspectionDate = formData.inspection_date && formData.inspection_date.trim();
        const hasInspectionTime = formData.inspection_time && formData.inspection_time.trim();
        
        if (hasInspectionDate && !hasInspectionTime) {
            errors.inspection_time = true;
            hasErrors = true;
            setError('عند إدخال تاريخ الفحص يجب إدخال وقت الفحص أيضاً');
            setValidationErrors(errors);
            return false;
        }
        
        if (hasInspectionTime && !hasInspectionDate) {
            errors.inspection_date = true;
            hasErrors = true;
            setError('عند إدخال وقت الفحص يجب إدخال تاريخ الفحص أيضاً');
            setValidationErrors(errors);
            return false;
        }

        if (hasErrors) {
            setValidationErrors(errors);
            setError('الرجاء ملء جميع الحقول المطلوبة');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsLoading(true);
        setError('');

        try {
            // Convert empty strings to null for optional fields
            const dataToSend = {
                customer: formData.customer || null,
                file_num: formData.file_num || null,
                goods: formData.goods || null,
                destination_port: formData.destination_port || null,
                shipment_type: formData.shipment_type || null,
                shipment_company: formData.shipment_company || null,
                expected_arrival_date: formData.expected_arrival_date || null,
                inspection_date: formData.inspection_date || null,
                inspection_time: formData.inspection_time || null,
                file_status: formData.file_status || null,
                driver: formData.driver || null,
                origin_country: formData.origin_country || null,
                custom_dec_num: formData.custom_dec_num || null,
                authority: formData.authority || null,
                file_type: formData.file_type || null,
                cost: formData.cost || 0,
                containers: containers.length > 0 ? containers : []
            };
            const response = await axiosInstance.post('/clearance-files/', dataToSend);
            console.log('File created with ID:', response.data?.id);
            console.log('Cost data to save:', costData);
            
            // If cost data exists, save it to the appropriate cost table
            if (costData && costData.data && response.data?.id) {
                const fileId = response.data.id;
                console.log('Saving cost data for file:', fileId, 'Type:', costData.type);
                try {
                    if (costData.type === 'SEA' || costData.type === 'AIR') {
                        const result = await axiosInstance.post(`/maritime-cost/${fileId}/`, costData.data);
                        console.log('Maritime/Air cost saved successfully:', result.data);
                    } else if (costData.type === 'LAND') {
                        const result = await axiosInstance.post(`/land-cost/${fileId}/`, costData.data);
                        console.log('Land cost saved successfully:', result.data);
                    }
                } catch (costErr) {
                    console.error('Error saving cost data:', costErr);
                    console.error('Error details:', costErr.response?.data);
                    // Don't fail the whole operation, file is created
                }
            } else {
                console.log('No cost data to save or missing file ID');
            }
            
            // Show WhatsApp notification choice modal for new file
            // Use response.data (saved data from API) instead of form state
            const savedFile = response.data;
            const customerPhone = savedFile.customer_phone;
            
            if (customerPhone) {
                // Get container numbers from saved data
                const containerNumbers = savedFile.containers_data?.map(c => c.number).filter(n => n) || [];
                
                // Show notification choice modal with saved data
                setNotificationChoiceModal({
                    isOpen: true,
                    notificationType: 'new-file',
                    notificationData: {
                        customer_phone: customerPhone,
                        file_num: savedFile.file_num || '',
                        container_numbers: containerNumbers,
                        expected_arrival_date: savedFile.expected_arrival_date || '',
                        file_cost: savedFile.cost || 0,
                        goods_description: savedFile.goods_description || ''
                    }
                });
                
                // Don't call onSuccess/handleClose yet - wait for modal dismissal
                onSuccess?.();
                return;
            }
            
            // No WhatsApp to send - close modal and refresh
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Error saving clearance file:', err);
            setError(err.response?.data?.error || 'حدث خطأ أثناء حفظ الملف');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            customer: '',
            file_num: '',
            goods: '',
            destination_port: '',
            shipment_type: '',
            shipment_company: '',
            expected_arrival_date: '',
            inspection_date: '',
            inspection_time: '',
            file_status: '',
            driver: '',
            origin_country: '',
            custom_dec_num: '',
            authority: '',
            file_type: '',
            cost: '0'
        });
        setContainers([]);
        setArrivalDate({ day: '', month: '', year: '' });
        setInspectionDate({ day: '', month: '', year: '' });
        setInspectionTime({ hour: '', minute: '' });
        setError('');
        setValidationErrors({});
        setCostData(null); // Reset cost data
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal modal-large add-file-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3 className="modal-title">إضافة ملف تخليص جديد</h3>
                        <button type="button" className="modal-close" onClick={handleClose}>
                            <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {/* Section 1: معلومات أساسية */}
                            <div className="form-section">
                                <h4 className="section-title">معلومات أساسية</h4>
                                <div className="form-grid">
                                    <SearchableDropdown
                                        label="الزبون"
                                        options={lookupData.customers}
                                        value={formData.customer}
                                        onChange={(val) => handleDropdownChange('customer', val)}
                                        onAddNew={() => openQuickAdd(
                                            'customer', 
                                            'إضافة زبون جديد', 
                                            '', 
                                            '/customers/',
                                            [
                                                { name: 'name', label: 'اسم الزبون', placeholder: 'أدخل اسم الزبون', required: true, type: 'text' },
                                                { name: 'phone_num', label: 'رقم الواتساب', placeholder: 'أدخل رقم الواتساب', required: true, type: 'phone' }
                                            ]
                                        )}
                                        onEdit={(item) => handleEdit(
                                            'customer',
                                            'إضافة زبون جديد',
                                            '',
                                            '/customers/',
                                            [
                                                { name: 'name', label: 'اسم الزبون', placeholder: 'أدخل اسم الزبون', required: true, type: 'text' },
                                                { name: 'phone_num', label: 'رقم الواتساب', placeholder: 'أدخل رقم الواتساب', required: true, type: 'phone' }
                                            ],
                                            item
                                        )}
                                        onDelete={(item) => handleDelete('/customers/', item)}
                                        addNewLabel="إضافة زبون جديد"
                                        required
                                        hasError={validationErrors.customer}
                                    />

                                    <div className="form-group">
                                        <label className="form-label">
                                            رقم ملف التخليص
                                        </label>
                                        <input
                                            type="text"
                                            name="file_num"
                                            className="form-input"
                                            value={formData.file_num}
                                            onChange={handleInputChange}
                                            placeholder="أدخل رقم ملف التخليص"
                                            style={{
                                                borderColor: validationErrors.file_num ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.file_num ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <SearchableDropdown
                                        label="وصف البضاعة"
                                        options={lookupData.goods}
                                        value={formData.goods}
                                        onChange={(val) => handleDropdownChange('goods', val)}
                                        onAddNew={() => openQuickAdd('goods', 'إضافة وصف بضائع جديدة', 'وصف البضائع', '/goods/')}
                                        onEdit={(item) => handleEdit('goods', 'إضافة وصف بضائع جديدة', 'وصف البضائع', '/goods/', null, item)}
                                        onDelete={(item) => handleDelete('/goods/', item)}
                                        displayKey="description"
                                        addNewLabel="إضافة وصف بضائع جديدة"
                                        required
                                        hasError={validationErrors.goods}
                                    />

                                    <SearchableDropdown
                                        label="نوع الملف"
                                        options={lookupData.file_types}
                                        value={formData.file_type}
                                        onChange={(val) => handleDropdownChange('file_type', val)}
                                        // onAddNew={() => openQuickAdd('file_type', 'إضافة نوع ملف جديد', 'نوع الملف', '/file-types/')}
                                        // onEdit={(item) => handleEdit('file_type', 'إضافة نوع ملف جديد', 'نوع الملف', '/file-types/', null, item)}
                                        // onDelete={(item) => handleDelete('/file-types/', item)}
                                        // addNewLabel="إضافة نوع جديد"
                                        required
                                        hasError={validationErrors.file_type}
                                    />
                                </div>
                            </div>

                            {/* Section 2: معلومات الشحن */}
                            <div className="form-section">
                                <h4 className="section-title">معلومات الشحن</h4>
                                <div className="form-grid">
                                    <SearchableDropdown
                                        label="ميناء الوجهة"
                                        options={lookupData.destination_ports}
                                        value={formData.destination_port}
                                        onChange={(val) => handleDropdownChange('destination_port', val)}
                                        onAddNew={() => openQuickAdd('port', 'إضافة ميناء جديد', 'اسم الميناء', '/destination-ports/')}
                                        onEdit={(item) => handleEdit('port', 'إضافة ميناء جديد', 'اسم الميناء', '/destination-ports/', null, item)}
                                        onDelete={(item) => handleDelete('/destination-ports/', item)}
                                        addNewLabel="إضافة ميناء جديد"
                                        hasError={validationErrors.destination_port}
                                    />

                                    <SearchableDropdown
                                        label="نوع الشحنة"
                                        options={lookupData.shipment_types}
                                        value={formData.shipment_type}
                                        onChange={(val) => handleDropdownChange('shipment_type', val)}
                                        onAddNew={() => openQuickAdd('shipment', 'إضافة نوع شحنة جديد', 'نوع الشحنة', '/shipment-types/')}
                                        onEdit={(item) => handleEdit('shipment', 'إضافة نوع شحنة جديد', 'نوع الشحنة', '/shipment-types/', null, item)}
                                        onDelete={(item) => handleDelete('/shipment-types/', item)}
                                        addNewLabel="إضافة نوع جديد"
                                        hasError={validationErrors.shipment_type}
                                    />

                                    <SearchableDropdown
                                        label="اسم شركة الشحن"
                                        options={lookupData.shipment_companies}
                                        value={formData.shipment_company}
                                        onChange={(val) => handleDropdownChange('shipment_company', val)}
                                        onAddNew={() => openQuickAdd('shipment_company', 'إضافة شركة شحن جديدة', 'اسم شركة الشحن', '/shipment-companies/')}
                                        onEdit={(item) => handleEdit('shipment_company', 'إضافة شركة شحن جديدة', 'اسم شركة الشحن', '/shipment-companies/', null, item)}
                                        onDelete={(item) => handleDelete('/shipment-companies/', item)}
                                        addNewLabel="إضافة شركة شحن جديدة"
                                        hasError={validationErrors.shipment_company}
                                    />

                                    <SearchableDropdown
                                        label="بلد المنشأ"
                                        options={lookupData.origin_countries}
                                        value={formData.origin_country}
                                        onChange={(val) => handleDropdownChange('origin_country', val)}
                                        onAddNew={() => openQuickAdd('origin_country', 'إضافة بلد منشأ جديد', 'اسم البلد', '/origin-countries/')}
                                        onEdit={(item) => handleEdit('origin_country', 'إضافة بلد منشأ جديد', 'اسم البلد', '/origin-countries/', null, item)}
                                        onDelete={(item) => handleDelete('/origin-countries/', item)}
                                        addNewLabel="إضافة بلد منشأ جديد"
                                        hasError={validationErrors.origin_country}
                                    />
                                </div>
                            </div>

                            {/* Section 2.5: معلومات الحاويات */}
                            <div className="form-section">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: containers.length > 0 ? '1rem' : '0' }}>
                                    <h4 className="section-title" style={{ marginBottom: 0 }}>معلومات الحاويات</h4>
                                    <button 
                                        type="button" 
                                        className="btn btn-accent"
                                        onClick={addContainer}
                                        style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                                    >
                                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                        </svg>
                                        <span>إضافة حاوية</span>
                                    </button>
                                </div>
                                
                                {containers.length > 0 && containers.map((container, index) => (
                                    <div key={index} style={{ 
                                        padding: '1.25rem', 
                                        border: '2px solid #e1e8ed', 
                                        borderRadius: '10px', 
                                        marginBottom: '1rem',
                                        background: '#f8f9fa'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                            <h5 style={{ margin: 0, color: '#03428d', fontWeight: '600' }}>حاوية {index + 1}</h5>
                                            <button 
                                                type="button"
                                                onClick={() => removeContainer(index)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#dc3545',
                                                    cursor: 'pointer',
                                                    padding: '0.25rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem',
                                                    fontSize: '0.9rem'
                                                }}
                                            >
                                                <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                حذف
                                            </button>
                                        </div>
                                        <div className="form-grid">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    رقم الحاوية
                                                    <span style={{ color: '#c33', marginRight: '4px' }}>*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-input"
                                                    value={container.number}
                                                    onChange={(e) => handleContainerChange(index, 'number', e.target.value)}
                                                    placeholder="أدخل رقم الحاوية"
                                                    style={{
                                                        borderColor: validationErrors[`container_${index}_number`] ? '#dc3545' : undefined,
                                                        borderWidth: validationErrors[`container_${index}_number`] ? '2px' : undefined
                                                    }}
                                                />
                                            </div>

                                            <SearchableDropdown
                                                label="نوع الحاوية"
                                                options={lookupData.container_types}
                                                value={container.container_type}
                                                onChange={(val) => handleContainerChange(index, 'container_type', val)}
                                                onAddNew={() => openQuickAdd('container_type', 'إضافة نوع حاوية جديد', 'نوع الحاوية', '/container-types/')}
                                                onEdit={(item) => handleEdit('container_type', 'إضافة نوع حاوية جديد', 'نوع الحاوية', '/container-types/', null, item)}
                                                onDelete={(item) => handleDelete('/container-types/', item)}
                                                addNewLabel="إضافة نوع جديد"
                                                required
                                                hasError={validationErrors[`container_${index}_container_type`]}
                                            />

                                            <div className="form-group">
                                                <label className="form-label">
                                                    وزن البضاعة
                                                    <span style={{ color: '#c33', marginRight: '4px' }}>*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    className="form-input"
                                                    value={container.cargo_weight}
                                                    onChange={(e) => handleContainerChange(index, 'cargo_weight', e.target.value)}
                                                    placeholder="أدخل وزن البضاعة"
                                                    style={{
                                                        borderColor: validationErrors[`container_${index}_cargo_weight`] ? '#dc3545' : undefined,
                                                        borderWidth: validationErrors[`container_${index}_cargo_weight`] ? '2px' : undefined
                                                    }}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label className="form-label">
                                                    عدد الباكج
                                                    <span style={{ color: '#c33', marginRight: '4px' }}>*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-input"
                                                    value={container.package_count}
                                                    onChange={(e) => handleContainerChange(index, 'package_count', e.target.value)}
                                                    placeholder="أدخل عدد الباكج"
                                                    style={{
                                                        borderColor: validationErrors[`container_${index}_package_count`] ? '#dc3545' : undefined,
                                                        borderWidth: validationErrors[`container_${index}_package_count`] ? '2px' : undefined
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Section 3: التواريخ والمواعيد */}
                            <div className="form-section">
                                <h4 className="section-title">التواريخ والمواعيد</h4>
                                
                                {/* First Row: تاريخ الوصول المتوقع + حالة الملف */}
                                <div className="form-grid" style={{ marginBottom: '1rem' }}>
                                    {/* تاريخ الوصول المتوقع */}
                                    <div className="form-group">
                                        <label className="form-label">
                                            تاريخ الوصول المتوقع
                                        </label>
                                        <div className={`custom-date-input ${validationErrors.expected_arrival_date ? 'has-error' : ''}`}>
                                            <div className="date-display">
                                                <div className="date-field day-field">
                                                    <span className="date-field-label">اليوم</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="DD"
                                                        value={arrivalDate.day}
                                                        onChange={(e) => handleDateChange('arrival', 'day', e.target.value)}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                <span className="date-separator">-</span>
                                                <div className="date-field month-field">
                                                    <span className="date-field-label">الشهر</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="MM"
                                                        value={arrivalDate.month}
                                                        onChange={(e) => handleDateChange('arrival', 'month', e.target.value)}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                <span className="date-separator">-</span>
                                                <div className="date-field year-field">
                                                    <span className="date-field-label">السنة</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="YYYY"
                                                        value={arrivalDate.year}
                                                        onChange={(e) => handleDateChange('arrival', 'year', e.target.value)}
                                                        maxLength={4}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="calendar-trigger"
                                                    onClick={() => document.getElementById('arrival-date-picker').showPicker()}
                                                >
                                                    <svg fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                <input
                                                    type="date"
                                                    id="arrival-date-picker"
                                                    className="hidden-date-input"
                                                    value={formData.expected_arrival_date}
                                                    onChange={(e) => handleDatePickerChange('arrival', e)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* حالة الملف */}
                                    <SearchableDropdown
                                        label="حالة الملف"
                                        options={lookupData.file_statuses}
                                        value={formData.file_status}
                                        onChange={(val) => handleDropdownChange('file_status', val)}
                                        onAddNew={() => openQuickAdd('status', 'إضافة حالة جديدة', 'حالة الملف', '/file-statuses/')}
                                        onEdit={(item) => handleEdit('status', 'إضافة حالة جديدة', 'حالة الملف', '/file-statuses/', null, item)}
                                        onDelete={(item) => handleDelete('/file-statuses/', item)}
                                        addNewLabel="إضافة حالة جديدة"
                                        hasError={validationErrors.file_status}
                                    />
                                </div>

                                {/* Second Row: تاريخ الفحص + وقت الفحص */}
                                <div className="form-grid">
                                    {/* تاريخ الفحص */}
                                    <div className="form-group">
                                        <label className="form-label">
                                            تاريخ الفحص
                                        </label>
                                        <div className={`custom-date-input ${validationErrors.inspection_date ? 'has-error' : ''}`}>
                                            <div className="date-display">
                                                <div className="date-field day-field">
                                                    <span className="date-field-label">اليوم</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="DD"
                                                        value={inspectionDate.day}
                                                        onChange={(e) => handleDateChange('inspection', 'day', e.target.value)}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                <span className="date-separator">-</span>
                                                <div className="date-field month-field">
                                                    <span className="date-field-label">الشهر</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="MM"
                                                        value={inspectionDate.month}
                                                        onChange={(e) => handleDateChange('inspection', 'month', e.target.value)}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                <span className="date-separator">-</span>
                                                <div className="date-field year-field">
                                                    <span className="date-field-label">السنة</span>
                                                    <input
                                                        type="text"
                                                        className="date-field-input"
                                                        placeholder="YYYY"
                                                        value={inspectionDate.year}
                                                        onChange={(e) => handleDateChange('inspection', 'year', e.target.value)}
                                                        maxLength={4}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="calendar-trigger"
                                                    onClick={() => document.getElementById('inspection-date-picker').showPicker()}
                                                >
                                                    <svg fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                <input
                                                    type="date"
                                                    id="inspection-date-picker"
                                                    className="hidden-date-input"
                                                    value={formData.inspection_date}
                                                    onChange={(e) => handleDatePickerChange('inspection', e)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* وقت الفحص */}
                                    <div className="form-group">
                                        <label className="form-label">
                                            وقت الفحص
                                        </label>
                                        <div className={`custom-time-input ${validationErrors.inspection_time ? 'has-error' : ''}`}>
                                            <div className="time-display">
                                                <div className="time-field">
                                                    <span className="time-field-label">الساعة</span>
                                                    <input
                                                        type="text"
                                                        className="time-field-input"
                                                        placeholder="00"
                                                        value={inspectionTime.hour}
                                                        onChange={(e) => handleTimeChange('hour', e.target.value)}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                <span className="time-separator">:</span>
                                                <div className="time-field">
                                                    <span className="time-field-label">الدقيقة</span>
                                                    <input
                                                        type="text"
                                                        className="time-field-input"
                                                        placeholder="00"
                                                        value={inspectionTime.minute}
                                                        onChange={(e) => handleTimeChange('minute', e.target.value)}
                                                        maxLength={2}
                                                    />
                                                </div>
                                                <span className="time-format-badge">24h</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 4: معلومات الشفير والجهات */}
                            <div className="form-section">
                                <h4 className="section-title">معلومات الشفير والجهات</h4>
                                <div className="form-grid">
                                    <SearchableDropdown
                                        label="الشفير"
                                        options={lookupData.drivers}
                                        value={formData.driver}
                                        onChange={(val) => handleDropdownChange('driver', val)}
                                        onAddNew={() => openQuickAdd(
                                            'driver', 
                                            'إضافة شفير جديد', 
                                            '', 
                                            '/drivers/',
                                            [
                                                { name: 'name', label: 'اسم الشفير', placeholder: 'أدخل اسم الشفير', required: true, type: 'text' },
                                                { name: 'phone_num', label: 'رقم الواتساب', placeholder: 'أدخل رقم الواتساب', required: true, type: 'phone' }
                                            ]
                                        )}
                                        onEdit={(item) => handleEdit(
                                            'driver',
                                            'إضافة شفير جديد',
                                            '',
                                            '/drivers/',
                                            [
                                                { name: 'name', label: 'اسم الشفير', placeholder: 'أدخل اسم الشفير', required: true, type: 'text' },
                                                { name: 'phone_num', label: 'رقم الواتساب', placeholder: 'أدخل رقم الواتساب', required: true, type: 'phone' }
                                            ],
                                            item
                                        )}
                                        onDelete={(item) => handleDelete('/drivers/', item)}
                                        addNewLabel="إضافة شفير جديد"
                                        hasError={validationErrors.driver}
                                    />

                                    <div className="form-group">
                                        <label className="form-label">
                                            رقم البيان الجمركي
                                        </label>
                                        <input
                                            type="text"
                                            name="custom_dec_num"
                                            className="form-input"
                                            value={formData.custom_dec_num}
                                            onChange={handleInputChange}
                                            placeholder="أدخل رقم البيان الجمركي"
                                            style={{
                                                borderColor: validationErrors.custom_dec_num ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.custom_dec_num ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <SearchableDropdown
                                        label="اسم المخلص"
                                        options={lookupData.authorities}
                                        value={formData.authority}
                                        onChange={(val) => handleDropdownChange('authority', val)}
                                        onAddNew={() => openQuickAdd('authority', 'إضافة مخلص جديد', 'اسم المخلص', '/authorities/')}
                                        onEdit={(item) => handleEdit('authority', 'إضافة مخلص جديد', 'اسم المخلص', '/authorities/', null, item)}
                                        onDelete={(item) => handleDelete('/authorities/', item)}
                                        addNewLabel="إضافة مخلص جديد"
                                        hasError={validationErrors.authority}
                                    />
                                </div>
                            </div>

            {/* Section 5: التكلفة */}
            <div className="form-section">
                <h4 className="section-title">التكلفة</h4>
                <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="form-group">
                        <label className="form-label">
                            التكلفة
                        </label>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                            <input
                                type="number"
                                step="0.01"
                                name="cost"
                                className="form-input"
                                value={formData.cost}
                                onChange={handleInputChange}
                                placeholder="0"
                                style={{
                                    width: '350px'
                                }}
                            />
                            <button
                                type="button"
                                className="btn btn-accent"
                                onClick={handleCalculateCost}
                                style={{ 
                                    whiteSpace: 'nowrap',
                                    padding: '0.6rem 1.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0v3a1 1 0 102 0v-3zm2-3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4 2a1 1 0 10-2 0v3a1 1 0 102 0v-3z" />
                                </svg>
                                <span>حساب تكلفة الملف</span>
                            </button>
                        </div>
                    </div>

                    
                </div>
            </div>

                            {error && (
                                <div className="error-message" style={{ marginTop: '1rem' }}>
                                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button 
                                type="submit" 
                                className="btn btn-accent btn-large"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
                                        <span>جاري الحفظ...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span>حفظ الملف</span>
                                    </>
                                )}
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-outline btn-large"
                                onClick={handleClose}
                                disabled={isLoading}
                            >
                                <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span>إلغاء</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Quick Add Modal */}
            <QuickAddModal
                isOpen={quickAddModal.isOpen}
                onClose={() => setQuickAddModal({ ...quickAddModal, isOpen: false })}
                onSave={handleQuickAddSave}
                title={quickAddModal.title}
                fieldLabel={quickAddModal.fieldLabel}
                fieldName={quickAddModal.type === 'goods' ? 'description' : 'name'}
                placeholder={`أدخل ${quickAddModal.fieldLabel}`}
                fields={quickAddModal.fields}
                editMode={quickAddModal.editMode}
                editData={quickAddModal.editData}
            />

            {/* Delete Confirmation/Warning Modal */}
            {deleteModal.isOpen && (
                <div className="modal-overlay" onClick={closeDeleteModal}>
                    <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header" style={{ background: deleteModal.hasError ? 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)' : 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' }}>
                            <h3 className="modal-title" style={{ color: 'white' }}>{deleteModal.hasError ? 'تنبيه' : 'تأكيد الحذف'}</h3>
                            <button 
                                type="button" 
                                className="modal-close" 
                                onClick={closeDeleteModal}
                                style={{ color: 'white' }}
                            >
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
                                {deleteModal.hasError ? (
                                    <>
                                        <svg 
                                            style={{ width: '80px', height: '80px', color: '#ffc107', margin: '0 auto 1.5rem' }} 
                                            fill="currentColor" 
                                            viewBox="0 0 20 20"
                                        >
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <p style={{ fontSize: '1.15rem', color: '#495057', marginBottom: '0.5rem', fontWeight: '600', lineHeight: '1.8' }}>
                                            {error}
                                        </p>
                                        <div style={{ 
                                            background: '#fff3cd', 
                                            border: '2px solid #ffc107', 
                                            borderRadius: '8px', 
                                            padding: '1rem', 
                                            marginTop: '1.5rem',
                                            textAlign: 'right'
                                        }}>
                                            <p style={{ fontSize: '0.95rem', color: '#856404', margin: 0, lineHeight: '1.6' }}>
                                                💡 <strong>ملاحظة:</strong> هذا العنصر مستخدم في ملفات تخليص موجودة
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <svg 
                                            style={{ width: '64px', height: '64px', color: '#dc3545', margin: '0 auto 1rem' }} 
                                            fill="currentColor" 
                                            viewBox="0 0 20 20"
                                        >
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <p style={{ fontSize: '1.1rem', color: '#2c3e50', marginBottom: '0.5rem', fontWeight: '600' }}>
                                            هل أنت متأكد من حذف
                                        </p>
                                        <p style={{ fontSize: '1.25rem', color: '#000', fontWeight: '700', marginBottom: '1rem' }}>
                                            "{deleteModal.item?.name || deleteModal.item?.description}"
                                        </p>
                                        <p style={{ fontSize: '0.95rem', color: '#6c757d' }}>
                                            لا يمكن التراجع عن هذا الإجراء
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            {deleteModal.hasError ? (
                                <button 
                                    type="button" 
                                    className="btn"
                                    onClick={closeDeleteModal}
                                    style={{ 
                                        flex: 1,
                                        maxWidth: '250px',
                                        background: 'linear-gradient(135deg, #03428d 0%, #0066cc 100%)',
                                        color: 'white',
                                        border: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.5rem',
                                        padding: '0.75rem 1.5rem',
                                        fontSize: '1rem',
                                        fontWeight: '600'
                                    }}
                                >
                                    <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <span>فهمت</span>
                                </button>
                            ) : (
                                <>
                                    <button 
                                        type="button" 
                                        className="btn"
                                        onClick={confirmDelete}
                                        style={{ 
                                            flex: 1,
                                            background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                            color: 'white',
                                            border: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '0.5rem',
                                            padding: '0.75rem 1.5rem',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}
                                    >
                                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <span>حذف</span>
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-outline"
                                        onClick={closeDeleteModal}
                                        style={{ 
                                            flex: 1,
                                            background: 'white',
                                            color: '#6c757d',
                                            border: '2px solid #e1e8ed',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '0.5rem',
                                            padding: '0.75rem 1.5rem',
                                            fontSize: '1rem',
                                            fontWeight: '600'
                                        }}
                                    >
                                        <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                        </svg>
                                        <span>إلغاء</span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Cost Calculation Modals */}
            {costModal.isOpen && (costModal.type === 'SEA' || costModal.type === 'AIR') && (
                <SeaCostModal 
                    key={`sea-${(costData?.type === 'SEA' || costData?.type === 'AIR') ? 'has-data' : 'no-data'}`}
                    isOpen={true}
                    onClose={() => setCostModal({ isOpen: false, type: '' })}
                    onApply={handleApplyCost}
                    isNewFile={true}
                    initialCostData={(costData?.type === 'SEA' || costData?.type === 'AIR') ? costData.data : null}
                />
            )}
            {costModal.isOpen && costModal.type === 'LAND' && (
                <LandCostModal 
                    key={`land-${costData?.type === 'LAND' ? 'has-data' : 'no-data'}`}
                    isOpen={true}
                    onClose={() => setCostModal({ isOpen: false, type: '' })}
                    onApply={handleApplyCost}
                    isNewFile={true}
                    initialCostData={costData?.type === 'LAND' ? costData.data : null}
                />
            )}

            {/* WhatsApp Notification Result Modal */}
            {/* WhatsApp Notification Choice Modal */}
            <NotificationChoiceModal
                isOpen={notificationChoiceModal.isOpen}
                onClose={() => {
                    setNotificationChoiceModal({ isOpen: false, notificationType: '', notificationData: null });
                    handleClose();
                }}
                notificationType={notificationChoiceModal.notificationType}
                notificationData={notificationChoiceModal.notificationData}
                onComplete={() => {
                    setNotificationChoiceModal({ isOpen: false, notificationType: '', notificationData: null });
                    handleClose();
                }}
            />
        </>
    );
};

export default AddClearanceFileModal;
